import {
  ComponentProps,
  createContext,
  Dispatch,
  SetStateAction,
  useContext,
  useState,
} from "react";
import {
  Accept,
  DropzoneInputProps,
  DropzoneRootProps,
  ErrorCode,
  FileRejection,
  FileWithPath,
  useDropzone,
} from "react-dropzone";
import { tv } from "tailwind-variants";

import { fileSizeToBytes } from "~/common/constants";
import { extractFileNameFromPath } from "~/common/utils/extractFileNameFromPath";
import { ListConsentDocumentResponse } from "~/providers/types/consentDocuments";
import { DocumentIcon, PlusIcon, TrashIcon } from "../icons";

const fileUploadVariants = tv({
  slots: {
    dropzone:
      "relative cursor-pointer rounded-lg text-center transition-colors",
    content:
      "flex flex-col items-center gap-4 border border-neutral-700 p-3 rounded-lg",
    list: "flex flex-col gap-2 border border-neutral-700 rounded-lg p-3",
    item: "flex items-center gap-2 rounded bg-neutral-5 px-4 py-3 text-sm font-medium text-gray-70",
  },
  variants: {
    dragActive: {
      true: {
        dropzone: "border-blue-400 bg-blue-50",
      },
    },
    dragReject: {
      true: {
        dropzone: "border-red-400 bg-red-50",
      },
    },
  },
  compoundVariants: [
    {
      dragActive: true,
      dragReject: true,
      class: {
        dropzone: "border-red-400 bg-red-50",
      },
    },
  ],
});

const { list, item, dropzone, content } = fileUploadVariants();

const ERROR_CODE_TO_MESSAGE_MAP: Record<ErrorCode, string> = {
  [ErrorCode.FileInvalidType]: "Only PDF files are supported.",
  [ErrorCode.FileTooLarge]: "Maximum file size is 10MB.",
  [ErrorCode.FileTooSmall]: "File too small.",
  [ErrorCode.TooManyFiles]: "Please upload only one file.",
};

interface FileUploadContext {
  files: File[];
  existingFile?: ListConsentDocumentResponse;
  setExistingFile: Dispatch<
    SetStateAction<ListConsentDocumentResponse | undefined>
  >;
  removeAllFiles: () => void;
  getDropzoneProps: <T extends DropzoneRootProps>(props?: T) => T;
  getInputProps: <T extends DropzoneInputProps>(props?: T) => T;
  isDragActive: boolean;
  isDragReject: boolean;
  maxFiles: number;
  hasMaxFiles: boolean;
  acceptedFiles: readonly FileWithPath[];
  fileRejections: readonly FileRejection[];
}

const FileUploadContext = createContext<FileUploadContext | null>(null);

export const useFileUploadContext = () => {
  const fileUploadContext = useContext(FileUploadContext);
  if (!fileUploadContext)
    throw Error(
      "useFileUploadContext can only be used inside a FileUploadContext.Provider",
    );
  return fileUploadContext;
};

interface RootProps extends ComponentProps<"div"> {
  initialFile?: ListConsentDocumentResponse;
  onFilesChange?: (files: File[]) => void;
  maxFiles?: number;
  maxSize?: number;
  accept?: Accept;
  error?: string;
}

const Root = ({
  children,
  initialFile,
  onFilesChange,
  maxFiles = 1,
  maxSize = fileSizeToBytes.fromMB(5),
  accept = {},
  error: externalError,
}: RootProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [existingFile, setExistingFile] = useState<
    ListConsentDocumentResponse | undefined
  >(initialFile);

  const onDrop = <T extends File>(
    acceptedFiles: T[],
    _rejectedFiles: FileRejection[],
  ) => {
    setFiles(acceptedFiles);
    onFilesChange?.(acceptedFiles);
  };

  const removeAllFiles = () => {
    setFiles([]);
    onFilesChange?.([]);
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    acceptedFiles,
    fileRejections,
  } = useDropzone({
    onDrop,
    maxFiles,
    maxSize,
    accept,
    multiple: maxFiles > 1,
  });

  const internalError =
    ERROR_CODE_TO_MESSAGE_MAP[fileRejections[0]?.errors[0]?.code as ErrorCode];
  const hasErrors = !!internalError || !!externalError;

  return (
    <FileUploadContext.Provider
      value={{
        files,
        existingFile,
        setExistingFile,
        removeAllFiles,
        getDropzoneProps: getRootProps,
        getInputProps,
        isDragActive,
        isDragReject,
        maxFiles,
        hasMaxFiles: files.length >= maxFiles,
        acceptedFiles,
        fileRejections,
      }}
    >
      <div>
        <div>{children}</div>
        {hasErrors && !acceptedFiles.length && (
          <p className="mt-2 text-xs text-error">
            {internalError ?? externalError}
          </p>
        )}
      </div>
    </FileUploadContext.Provider>
  );
};

const Dropzone = ({ className, children }: ComponentProps<"div">) => {
  const {
    getDropzoneProps,
    getInputProps,
    isDragActive,
    isDragReject,
    acceptedFiles,
  } = useFileUploadContext();

  return (
    <div
      {...getDropzoneProps()}
      className={dropzone({
        dragActive: isDragActive,
        dragReject: isDragReject && !isDragActive && !acceptedFiles.length,
        className,
      })}
    >
      <input {...getInputProps()} />
      {children}
    </div>
  );
};

const Content = () => {
  const { files, existingFile } = useFileUploadContext();

  if (files.length || existingFile) return null;

  return (
    <div className={content()}>
      <div className="w-fit rounded-full bg-neutral-5 p-1">
        <PlusIcon className="size-7 stroke-2 text-gray-35" />
      </div>
      <div className="flex flex-col gap-1">
        <p className="font-medium text-gray-70">
          Drag and drop or upload consent document (PDF)
        </p>
        <p className="text-sm text-gray-70">Max file size: 10MB</p>
      </div>
    </div>
  );
};

const List = ({ className }: ComponentProps<"ul">) => {
  const { files, existingFile, setExistingFile, removeAllFiles } =
    useFileUploadContext();

  if (!files.length && !existingFile) return null;

  const filesOrExisting = files.length ? files : [existingFile];

  const handleDelete = () => {
    if (files.length) {
      removeAllFiles();
    } else {
      setExistingFile(undefined);
    }
  };

  return (
    <ul className={list({ className })}>
      {filesOrExisting.map((file, index) => (
        <Item className={item({ className })} key={index} file={file} />
      ))}
      <button
        type="button"
        onClick={handleDelete}
        className="ml-auto mt-11 flex items-center gap-2 text-sm font-medium text-error"
      >
        <TrashIcon />
        Delete
      </button>
    </ul>
  );
};

interface ItemProps extends ComponentProps<"li"> {
  file?: File | ListConsentDocumentResponse;
}

const Item = ({ file, className }: ItemProps) => {
  if (!file) return null;

  const fileName =
    file instanceof File
      ? file.name
      : extractFileNameFromPath(file.files[0].path);

  return (
    <li className={item({ className })}>
      <DocumentIcon className="size-5" />
      {fileName}
    </li>
  );
};

export const FileUpload = {
  Root,
  Dropzone,
  Content,
  List,
};
